/**
 * AuditReportPage — public, read-only Full Audit report reachable via a share
 * token (no login). Mirrors the in-app report: scores, net variance, findings
 * and resolutions.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { formatPHP } from '../lib/utils';
import { ShieldCheck, AlertTriangle, Loader2, Boxes, Wallet, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEV = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  watch: 'bg-blue-50 text-blue-700 border-blue-200',
  info: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function AuditReportPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/audit-wizard/shared/${token}`);
        setData(res.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Report not found');
      }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 size={24} className="animate-spin text-[#1A4D2E]" /></div>;
  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-6">
      <AlertTriangle size={32} className="text-amber-500 mb-3" />
      <p className="font-semibold text-slate-700">{err}</p>
    </div>
  );

  const m = data.meta || {};
  const sc = data.scores || {};
  const findings = data.findings || [];
  const cashVar = data.cash?.physical_variance ?? data.cash?.discrepancy ?? 0;
  const stockVar = data.inventory?.available ? (data.inventory?.summary?.total_variance_capital ?? 0) : null;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-audit-report">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="bg-[#1A4D2E] text-white rounded-xl p-6">
          <div className="flex items-center gap-2 mb-1"><ShieldCheck size={18} /><span className="text-xs font-bold uppercase tracking-[0.14em] text-white/70">Full Audit Report</span></div>
          <h1 className="text-2xl font-bold">{m.branch_name}</h1>
          <p className="text-sm text-white/70 mt-1">{m.period_from} → {m.period_to} · {m.mode} audit</p>
          {m.signed_off_by && <p className="text-xs text-white/60 mt-2">Signed off by {m.signed_off_by} · {String(m.finalized_at || '').slice(0, 10)}</p>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Health" value={sc.health_score ?? '—'} sub={sc.health_label} />
          <Stat label="Risk" value={sc.fraud_risk_score ?? '—'} sub={sc.fraud_risk_label} />
          <Stat label="Cash" value={formatPHP(cashVar)} icon={<Wallet size={13} />} tone={cashVar < 0 ? 'bad' : cashVar > 0 ? 'good' : ''} />
          <Stat label="Stock" value={stockVar == null ? 'n/a' : formatPHP(stockVar)} icon={<Boxes size={13} />} tone={stockVar < 0 ? 'bad' : stockVar > 0 ? 'good' : ''} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Findings ({findings.length})</div>
          {findings.length === 0 ? (
            <div className="p-8 flex flex-col items-center text-center"><CheckCircle2 size={28} className="text-emerald-500 mb-2" /><p className="text-sm text-slate-500">No findings — clean audit.</p></div>
          ) : findings.map((f) => (
            <div key={f.id} className="px-4 py-3 border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${SEV[f.severity] || SEV.info}`}>{f.severity}</span>
                <p className="text-sm font-medium text-slate-700 flex-1">{f.title}</p>
                {f.amount_impact ? <span className={`font-mono text-xs font-semibold ${f.amount_impact < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatPHP(f.amount_impact)}</span> : null}
              </div>
              {f.recommendation && <p className="text-xs text-slate-500 mt-1 pl-1">{f.recommendation}</p>}
            </div>
          ))}
        </div>

        {(data.resolutions || []).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Resolutions</div>
            {data.resolutions.map((r, i) => (
              <div key={i} className="px-4 py-2 flex items-center justify-between gap-3 text-xs border-b border-slate-50 last:border-0">
                <span className="text-slate-600 truncate flex-1">{r.title}</span>
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold capitalize">{r.resolution.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-center text-[11px] text-slate-400">Read-only audit report · AgriBooks</p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon, tone }) {
  const color = tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-emerald-600' : 'text-slate-800';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5 flex items-center justify-center gap-1">{icon}{label}</p>
      <p className={`text-base font-bold font-mono ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
